import { IFAS_PORTAL_URL, REDIRECT_SIGNIN_URL } from "../services/config";

/**
 * Helpers for the claims admin login.
 *
 * Was `utils/workspace.ts` until the estate launcher moved to its own portal
 * (workspace-portal). What remained had nothing to do with the workspace — it
 * is the plumbing that keeps /admin/login on the origin Cognito is configured
 * to call back to — so the file was renamed to say so.
 */

export type ClaimsAdminPortal = "claims" | "ifas";

/**
 * The origin Cognito will redirect back to after sign-in.
 *
 * Derived from the configured callback URL rather than the current location,
 * so an admin login opened on any other host still lands on the origin
 * registered with the app client.
 */
export const getClaimsOrigin = () => {
  if (!REDIRECT_SIGNIN_URL) {
    return typeof window === "undefined" ? "" : window.location.origin;
  }

  try {
    return new URL(REDIRECT_SIGNIN_URL).origin;
  } catch {
    return typeof window === "undefined" ? "" : window.location.origin;
  }
};

export const normalizeClaimsAdminPortal = (
  portal: string | null
): ClaimsAdminPortal => (portal === "ifas" ? "ifas" : "claims");

export const getClaimsAdminLoginUrl = (portal: ClaimsAdminPortal) => {
  const origin = getClaimsOrigin();
  const search = new URLSearchParams({ portal });

  return `${origin}/admin/login?${search.toString()}`;
};

/**
 * The IFAS portal's admin login, or null where IFAS is not live.
 *
 * IFAS moved to its own app and host (#2089). The Funerals admin login used to
 * be this app's /admin/login?portal=ifas; old bookmarks and the IFAS-role
 * redirect both land here.
 */
export const getIfasPortalLoginUrl = (): string | null => {
  if (!IFAS_PORTAL_URL) return null;
  return `${IFAS_PORTAL_URL.replace(/\/+$/, "")}/admin/login?portal=ifas`;
};
